import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAdminProducts, reset, deleteProduct, restoreProduct } from '../../features/product/productSlice';
import { toast } from 'react-toastify';
import Loading from '../../components/Loading';
import { formatPrice as formatCurrency, getStoredMarket } from '../../utils/currency';

function AdminProducts() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [categoryFilter, setCategoryFilter] = useState('all');
    const isTrashView = location.pathname.endsWith('/trash');
    const market = getStoredMarket();
    
    const { user } = useSelector((state) => state.auth);
    const { products, isLoading, isError, message } = useSelector((state) => state.product);

    // Fetch products on component mount
    useEffect(() => {
        if (isError) {
            toast.error(message);
        }
        
        // Redirect if user is not logged in
        if (!user) {
            navigate('/login');
        } else {
           dispatch(getAdminProducts());
        }

        // Cleanup function to reset state on unmount
        return () => {
            dispatch(reset());
        };
    }, [user, navigate, isError, message, dispatch]);

    // Renamed function to avoid conflict with imported deleteProduct
    const handleDeleteProduct = (id) => {
        if (window.confirm('Are you sure you want to delete this product?')) {
            dispatch(deleteProduct(id));
        }
    };

    const handleRestoreProduct = (id) => {
        dispatch(restoreProduct(id));
    };

    // Helper to find the minimum price among variants
    const getMinPrice = (variants) => {
        if (!variants || variants.length === 0) return 'N/A';
        const min = Math.min(...variants.map(v => v.price));
        return formatCurrency(min, { market });
    };

    // Helper to calculate total stock
    const getTotalStock = (variants) => {
        if (!variants || variants.length === 0) return 0;
        return variants.reduce((acc, v) => acc + v.countInStock, 0);
    };

    const categories = useMemo(() => {
        if (!products) return [];
        const unique = new Set();
        products.forEach((product) => {
            if (product.category) {
                unique.add(product.category);
            }
        });
        return Array.from(unique).sort();
    }, [products]);

    const productsByStatus = useMemo(() => {
        if (!products) return [];
        return products.filter((product) => isTrashView ? product.status === 'inactive' : product.status === 'active');
    }, [products, isTrashView]);

    const filteredProducts = useMemo(() => {
        if (!productsByStatus) return [];
        if (categoryFilter === 'all') return productsByStatus;
        return productsByStatus.filter((product) => product.category === categoryFilter);
    }, [productsByStatus, categoryFilter]);

    if (isLoading) {
        return <Loading />;
    }

    return (
        <div className="admin-container">
            <h1 className="admin-header">{isTrashView ? 'Product Trash' : 'Product List'}</h1>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                    gap: '12px',
                    flexWrap: 'wrap'
                }}
            >
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {!isTrashView && (
                        <button className="btn btn-primary simple my-2" onClick={() => navigate('/admin/add-product')}>
                            Add New Product
                        </button>
                    )}
                    <button
                        className="btn btn-secondary simple my-2"
                        onClick={() => navigate(isTrashView ? '/admin/products' : '/admin/products/trash')}
                    >
                        {isTrashView ? 'Back to Products' : 'View Trash'}
                    </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label htmlFor="productCategoryFilter" style={{ fontWeight: 600 }}>Filter:</label>
                    <select
                        id="productCategoryFilter"
                        className="form-select"
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        style={{ minWidth: '200px' }}
                    >
                        <option value="all">All categories</option>
                        {categories.map((category) => (
                            <option key={category} value={category}>
                                {category}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <table className="admin-table">
                <thead>
                    <tr>
                        <th>Image</th>
                        <th>Title</th>
                        <th>Category</th>
                        <th>Country</th>
                        <th>Price (from)</th>
                        <th>Total Stock</th>
                        <th>Status</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredProducts && filteredProducts.length > 0 ? (
                        filteredProducts.map((product) => (
                            <tr key={product._id}>
                                <td onClick={() => navigate('/admin/add-product/' + product._id)} style={{ cursor: 'pointer' }}>
                                    {product.image && (
                                        <img 
                                            src={product.image.src} 
                                            alt={product.title} 
                                            style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }} 
                                        />
                                    )}
                                </td>
                                <td onClick={() => navigate('/admin/add-product/' + product._id)} style={{ cursor: 'pointer' }}>
                                    {product.title}
                                </td>
                                <td onClick={() => navigate('/admin/add-product/' + product._id)} style={{ cursor: 'pointer' }}>
                                    {product.category}
                                </td>
                                <td onClick={() => navigate('/admin/add-product/' + product._id)} style={{ cursor: 'pointer' }}>
                                    {(product.countries && product.countries.length) ? product.countries.join(', ') : 'N/A'}
                                </td>
                                <td onClick={() => navigate('/admin/add-product/' + product._id)} style={{ cursor: 'pointer' }}>
                                    {getMinPrice(product.variants)}
                                </td>
                                <td onClick={() => navigate('/admin/add-product/' + product._id)} style={{ cursor: 'pointer' }}>
                                    {getTotalStock(product.variants)}
                                </td>
                                <td onClick={() => navigate('/admin/add-product/' + product._id)} style={{ cursor: 'pointer' }}>
                                    {product.status}
                                </td>
                                <td>
                                    {isTrashView ? (
                                        <button
                                            className="btn btn-primary"
                                            onClick={() => handleRestoreProduct(product._id)}
                                        >
                                            Renew
                                        </button>
                                    ) : (
                                        <>
                                            <button 
                                                className="btn btn-secondary" 
                                                onClick={() => navigate('/admin/add-product/' + product._id)}
                                                style={{ marginRight: '8px' }}
                                            >
                                                Edit
                                            </button>
                                            <button 
                                                className="btn btn-danger" 
                                                onClick={() => handleDeleteProduct(product._id)}
                                            >
                                                Delete
                                            </button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>
                                No products found.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

export default AdminProducts;
